/**
 * 设计系统ViewModel
 * 管理设计系统相关的状态和业务逻辑
 */

import { useState, useCallback, useContext, createContext } from 'react';
import { DesignSystemModel, ComponentStateModel, designSystem } from '../models/DesignSystemModel.js';

// 设计系统上下文
const DesignSystemContext = createContext();

/**
 * 设计系统Provider组件
 * @param {Object} props - 组件属性
 * @param {React.ReactNode} props.children - 子组件
 * @param {DesignSystemModel} props.designSystem - 设计系统实例
 */
export function DesignSystemProvider({ children, designSystem: customDesignSystem }) {
  const [currentDesignSystem] = useState(customDesignSystem || designSystem);
  const [componentStates, setComponentStates] = useState(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 获取组件状态
   * @param {string} componentId - 组件ID
   * @returns {ComponentStateModel} 组件状态
   */
  const getComponentState = useCallback((componentId) => {
    return componentStates.get(componentId) || new ComponentStateModel({ name: componentId });
  }, [componentStates]);

  /**
   * 设置组件状态
   * @param {string} componentId - 组件ID
   * @param {Object} newState - 新状态
   */
  const setComponentState = useCallback((componentId, newState) => {
    setComponentStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(componentId) || new ComponentStateModel({ name: componentId });
      currentState.updateState(newState);
      newMap.set(componentId, currentState);
      return newMap;
    });
  }, []);

  /**
   * 重置组件状态
   * @param {string} componentId - 组件ID
   */
  const resetComponentState = useCallback((componentId) => {
    setComponentStates(prev => {
      const newMap = new Map(prev);
      const currentState = prev.get(componentId);
      if (currentState) {
        currentState.reset();
        newMap.set(componentId, currentState);
      }
      return newMap;
    });
  }, []);

  /**
   * 获取设计令牌值
   * @param {string} category - 令牌类别
   * @param {string} name - 令牌名称
   * @returns {string} 令牌值
   */
  const getTokenValue = useCallback((category, name) => {
    const token = currentDesignSystem.getToken(category, name);
    return token ? token.value : '';
  }, [currentDesignSystem]);

  /**
   * 获取组件配置
   * @param {string} componentName - 组件名称
   * @returns {Object} 组件配置
   */
  const getComponentConfig = useCallback((componentName) => {
    return currentDesignSystem.getComponent(componentName);
  }, [currentDesignSystem]);

  /**
   * 应用设计令牌到DOM
   */
  const applyTokens = useCallback(() => {
    setIsLoading(true);
    setError(null);
    
    try {
      currentDesignSystem.applyTokensToDOM();
    } catch (err) {
      setError(err.message || '应用设计令牌失败');
    } finally {
      setIsLoading(false);
    }
  }, [currentDesignSystem]);

  /**
   * 导出设计系统配置
   * @returns {Object} 配置对象
   */
  const exportConfig = useCallback(() => {
    return currentDesignSystem.export();
  }, [currentDesignSystem]);

  /**
   * 生成CSS变量
   * @returns {string} CSS变量字符串
   */
  const generateCSS = useCallback(() => {
    return currentDesignSystem.generateCSSVariables();
  }, [currentDesignSystem]);

  // 上下文值
  const contextValue = {
    // 状态
    designSystem: currentDesignSystem,
    componentStates,
    isLoading,
    error,
    
    // 操作方法
    getComponentState,
    setComponentState,
    resetComponentState,
    getTokenValue,
    getComponentConfig,
    applyTokens,
    exportConfig,
    generateCSS
  };

  return (
    <DesignSystemContext.Provider value={contextValue}>
      {children}
    </DesignSystemContext.Provider>
  );
}

/**
 * 设计系统ViewModel Hook
 * @returns {Object} 设计系统状态和操作方法
 */
export function useDesignSystemViewModel() {
  const context = useContext(DesignSystemContext);
  
  if (!context) {
    throw new Error('useDesignSystemViewModel must be used within a DesignSystemProvider');
  }
  
  return context;
}

/**
 * 组件状态Hook
 * @param {string} componentName - 组件名称
 * @param {string} instanceId - 实例ID（可选）
 * @returns {Object} 组件状态和操作方法
 */
export function useComponentState(componentName, instanceId = 'default') {
  const { getComponentState, setComponentState, resetComponentState, getComponentConfig } = useDesignSystemViewModel();
  
  const componentId = `${componentName}-${instanceId}`;
  const state = getComponentState(componentId);
  const config = getComponentConfig(componentName);

  /**
   * 更新状态
   * @param {Object} newState - 新状态
   */
  const updateState = useCallback((newState) => {
    setComponentState(componentId, newState);
  }, [componentId, setComponentState]);

  /**
   * 重置状态
   */
  const resetState = useCallback(() => {
    resetComponentState(componentId);
  }, [componentId, resetComponentState]);

  /**
   * 设置变体
   * @param {string} variant - 变体名称
   */
  const setVariant = useCallback((variant) => {
    updateState({ variant });
  }, [updateState]);

  /**
   * 设置尺寸
   * @param {string} size - 尺寸名称
   */
  const setSize = useCallback((size) => {
    updateState({ size });
  }, [updateState]);

  return { state, config, updateState, resetState, setVariant, setSize };
}